/**
 * The permission matrix (Step 15): module × action, plus a data scope per module. Browser-safe;
 * the API and the web app use this same file. Change a built-in role here only.
 */
export const MODULES = [
  'leads',
  'lists',
  'campaigns',
  'mailboxes',
  'domains',
  'inbox',
  'opportunities',
  'analytics',
  'deliverability',
  'settings',
  'users',
  'integrations',
  'billing',
] as const;
export type Module = (typeof MODULES)[number];

export const ACTIONS = ['view', 'create', 'edit', 'delete', 'export', 'manage'] as const;
export type Action = (typeof ACTIONS)[number];

/** All = every record; Team = mine and my teams' members'; Own = mine only. */
export const SCOPES = ['all', 'team', 'own'] as const;
export type Scope = (typeof SCOPES)[number];

/** The actions that mean something for each module (the matrix editor shows only these). */
export const MODULE_ACTIONS = {
  leads: ['view', 'create', 'edit', 'delete', 'export', 'manage'],
  lists: ['view', 'create', 'edit', 'delete', 'export'],
  campaigns: ['view', 'create', 'edit', 'delete', 'export'],
  mailboxes: ['view', 'create', 'edit', 'delete', 'manage'],
  domains: ['view', 'create', 'edit', 'delete'],
  inbox: ['view', 'create', 'edit', 'manage'],
  opportunities: ['view', 'edit', 'export', 'manage'],
  analytics: ['view', 'export'],
  deliverability: ['view', 'manage'],
  settings: ['view', 'manage'],
  users: ['view', 'create', 'edit', 'delete', 'manage'],
  integrations: ['view', 'create', 'edit', 'delete'],
  billing: ['view', 'manage'],
} as const satisfies Record<Module, readonly Action[]>;

/** Modules whose records have an owner, so All / Team / Own applies. */
export const SCOPED_MODULES: readonly Module[] = [
  'leads',
  'lists',
  'campaigns',
  'mailboxes',
  'inbox',
  'opportunities',
  'analytics',
];

export const MODULE_LABELS: Readonly<Record<Module, string>> = {
  leads: 'Leads',
  lists: 'Lists',
  campaigns: 'Campaigns',
  mailboxes: 'Mailboxes',
  domains: 'Domains',
  inbox: 'Inbox',
  opportunities: 'Opportunities',
  analytics: 'Analytics',
  deliverability: 'Deliverability',
  settings: 'Settings',
  users: 'Users',
  integrations: 'Integrations',
  billing: 'Billing',
};

export const ACTION_LABELS: Readonly<Record<Action, string>> = {
  view: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  export: 'Export',
  manage: 'Manage',
};

export const SCOPE_LABELS: Readonly<Record<Scope, string>> = {
  all: 'All',
  team: 'Team',
  own: 'Own',
};

/** What each cell allows, in plain words (tooltips in the matrix editor). */
export const ACTION_HELP: Readonly<Partial<Record<Module, Partial<Record<Action, string>>>>> = {
  leads: {
    view: 'See leads, imports and the suppression list',
    create: 'Add leads and import files',
    edit: 'Edit leads, tags and owners; start verification',
    delete: 'Delete leads',
    export: 'Export leads, the suppression list and verification results (personal data)',
    manage: 'Add to the suppression list; the auto-suppress setting',
  },
  lists: {
    view: 'See lists',
    create: 'Create lists',
    edit: 'Rename lists, add and remove leads',
    delete: 'Delete lists',
    export: 'Export a list',
  },
  campaigns: {
    view: 'See campaigns, their numbers and activity',
    create: 'Create and duplicate campaigns',
    edit: 'Build, launch, pause and resume; add and remove leads',
    delete: 'Delete campaigns (a running one is paused first; its leads stay in Leads)',
    export: 'Export campaign results',
  },
  mailboxes: {
    view: 'See mailboxes, send logs and warm-up',
    create: 'Add and connect mailboxes',
    edit: 'Edit, test, pause; switch warm-up on or off',
    delete: 'Delete mailboxes',
    manage: 'Override warm-up readiness and health levels',
  },
  domains: {
    view: 'See sending domains and DNS checks',
    create: 'Add domains',
    edit: 'Edit, check now, pause',
    delete: 'Delete domains',
  },
  inbox: {
    view: 'Read replies',
    create: 'Reply from the Inbox (when the setting is on)',
    edit: 'Change categories, mark read',
    manage: 'Reply settings: Use AI, reply from Inbox',
  },
  opportunities: {
    view: 'See opportunities',
    edit: 'Change stage and notes',
    export: 'Export opportunities',
    manage: 'Change the owner',
  },
  analytics: { view: 'See reports and numbers', export: 'Export reports' },
  deliverability: {
    view: 'See health, blocklists and inbox placement',
    manage: 'The Sending page, Check now / Test now, seed inboxes, Postmaster, overrides',
  },
  settings: {
    view: 'See workspace settings',
    manage:
      'Workspaces, verification, Pause all sending, dry run, unknown sends; remove suppression entries',
  },
  users: {
    view: 'See the team',
    create: 'Invite people',
    edit: 'Change roles, teams and workspace access',
    delete: 'Remove or deactivate people',
    manage: 'Roles, teams and reassigning records',
  },
  integrations: {
    view: 'See API keys and webhooks',
    create: 'Create API keys and webhooks',
    edit: 'Edit and test webhooks',
    delete: 'Revoke API keys, delete webhooks',
  },
  billing: { view: 'See the plan and invoices', manage: 'Change the plan and payment' },
};

export interface ModuleGrant {
  actions: Action[];
  scope: Scope;
}
/** A role's rights. A module that is missing = no access at all. */
export type Matrix = Partial<Record<Module, ModuleGrant>>;

/** A permission is "<module>.<action>", e.g. "leads.export"; only the cells that exist. */
export type Permission = {
  [M in Module]: `${M}.${(typeof MODULE_ACTIONS)[M][number]}`;
}[Module];

export function permissionParts(permission: Permission): [Module, Action] {
  const [module, action] = permission.split('.') as [Module, Action];
  return [module, action];
}

/**
 * Never with an API key: managing people and integrations, and settings that change how the
 * whole workspace sends (so a leaked key can never add people, keys or stop sending).
 */
export const SESSION_ONLY: ReadonlySet<Permission> = new Set<Permission>([
  'users.create',
  'users.edit',
  'users.delete',
  'users.manage',
  'integrations.create',
  'integrations.edit',
  'integrations.delete',
  'settings.manage',
  'billing.manage',
]);

const SCOPE_RANK: Record<Scope, number> = { all: 3, team: 2, own: 1 };

/** Does the matrix allow this action on this module? */
export function matrixAllows(matrix: Matrix, module: Module, action: Action): boolean {
  return matrix[module]?.actions.includes(action) ?? false;
}

/** Is every right in `candidate` also in `actor` (same or wider scope)? (No escalation.) */
export function matrixWithin(candidate: Matrix, actor: Matrix): boolean {
  return MODULES.every((m) => {
    const c = candidate[m];
    if (!c || c.actions.length === 0) return true;
    const a = actor[m];
    if (!a) return false;
    if (!c.actions.every((x) => a.actions.includes(x))) return false;
    return !SCOPED_MODULES.includes(m) || SCOPE_RANK[c.scope] <= SCOPE_RANK[a.scope];
  });
}

/** Only what both allow, with the narrower scope (an API key under its creator's rights). */
export function matrixIntersect(a: Matrix, b: Matrix): Matrix {
  const out: Matrix = {};
  for (const m of MODULES) {
    const x = a[m];
    const y = b[m];
    if (!x || !y) continue;
    const actions = x.actions.filter((act) => y.actions.includes(act));
    if (actions.length === 0) continue;
    out[m] = { actions, scope: SCOPE_RANK[x.scope] <= SCOPE_RANK[y.scope] ? x.scope : y.scope };
  }
  return out;
}

/**
 * A clean matrix from untrusted input (the roles editor): known modules and actions only, no
 * action without "view", scope All for modules without owners.
 */
export function normalizeMatrix(input: unknown): Matrix {
  const out: Matrix = {};
  if (!input || typeof input !== 'object') return out;
  for (const m of MODULES) {
    const raw = (input as Record<string, unknown>)[m] as
      { actions?: unknown; scope?: unknown } | undefined;
    if (!raw || !Array.isArray(raw.actions)) continue;
    const actions: Action[] = (MODULE_ACTIONS[m] as readonly Action[]).filter((a) =>
      (raw.actions as unknown[]).includes(a),
    );
    if (actions.length === 0) continue;
    if (!actions.includes('view')) actions.unshift('view');
    const scope =
      SCOPED_MODULES.includes(m) && SCOPES.includes(raw.scope as Scope)
        ? (raw.scope as Scope)
        : 'all';
    out[m] = { actions, scope };
  }
  return out;
}

const every = (scope: Scope = 'all', except: readonly Module[] = []): Matrix =>
  Object.fromEntries(
    MODULES.filter((m) => !except.includes(m)).map((m) => [
      m,
      { actions: [...(MODULE_ACTIONS[m] as readonly Action[])], scope },
    ]),
  );
const grant = (actions: Action[], scope: Scope = 'all'): ModuleGrant => ({ actions, scope });
const full = (m: Module, scope: Scope): ModuleGrant =>
  grant([...(MODULE_ACTIONS[m] as readonly Action[])], scope);

/**
 * Built-in roles. "manager" and "member" are the roles from before Step 15 ("(old)"): they keep
 * those people's rights unchanged; new people get the new roles.
 */
export const BUILTIN_ROLES = [
  'owner',
  'admin',
  'campaign_manager',
  'sales_rep',
  'viewer',
  'manager',
  'member',
] as const;
export type BuiltinRole = (typeof BUILTIN_ROLES)[number];
export const LEGACY_ROLES: readonly BuiltinRole[] = ['manager', 'member'];

export const BUILTIN_MATRICES: Readonly<Record<BuiltinRole, Matrix>> = {
  owner: every(),
  admin: every('all', ['billing']),
  campaign_manager: {
    leads: full('leads', 'team'),
    lists: full('lists', 'team'),
    campaigns: full('campaigns', 'team'),
    mailboxes: full('mailboxes', 'team'),
    inbox: full('inbox', 'team'),
    analytics: grant(['view', 'export'], 'team'),
    deliverability: grant(['view']),
    domains: grant(['view']),
    opportunities: grant(['view', 'edit'], 'team'),
  },
  // Step 17b: opportunities have no personal owner (the CRM follows up), so a Sales Rep sees
  // every reply and opportunity in the Inbox.
  sales_rep: {
    inbox: grant(['view', 'create', 'edit']),
    opportunities: grant(['view', 'edit']),
    leads: grant(['view'], 'own'),
    analytics: grant(['view'], 'own'),
  },
  viewer: Object.fromEntries(
    MODULES.filter((m) => m !== 'billing').map((m) => [m, grant(['view'])]),
  ),
  manager: {
    users: grant(['view']),
    domains: grant(['view', 'create', 'edit']),
    mailboxes: grant(['view', 'create', 'edit']),
    leads: grant(['view', 'create', 'edit', 'delete', 'export', 'manage']),
    lists: grant(['view', 'create', 'edit', 'delete', 'export']),
    campaigns: grant(['view', 'create', 'edit', 'delete']),
    inbox: grant(['view', 'create', 'edit']),
    opportunities: grant(['view', 'edit', 'manage']),
    analytics: grant(['view']),
    deliverability: grant(['view']),
  },
  member: {
    users: grant(['view']),
    domains: grant(['view']),
    mailboxes: grant(['view']),
    leads: grant(['view', 'create', 'edit']),
    lists: grant(['view', 'create', 'edit']),
    campaigns: grant(['view', 'create', 'edit', 'delete']),
    inbox: grant(['view', 'create', 'edit']),
    opportunities: grant(['view', 'edit', 'manage']),
    analytics: grant(['view']),
  },
};

export const BUILTIN_ROLE_LABELS: Readonly<Record<BuiltinRole, string>> = {
  owner: 'Owner',
  admin: 'Admin',
  campaign_manager: 'Campaign Manager',
  sales_rep: 'Sales Rep',
  viewer: 'Viewer',
  manager: 'Manager (old)',
  member: 'Member (old)',
};

export const BUILTIN_ROLE_DESCRIPTIONS: Readonly<Record<BuiltinRole, string>> = {
  owner: 'Everything, including billing and owners',
  admin: 'Everything except billing; manages people, roles and settings',
  campaign_manager: 'Runs campaigns, leads and mailboxes for their team; no settings or users',
  sales_rep: 'Every reply in the Inbox, and their own leads',
  viewer: 'Sees everything, changes nothing',
  manager: 'The Manager role from before roles were customisable',
  member: 'The Member role from before roles were customisable',
};
